import { post, get } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';

/**
 * Service for handling survival analysis API calls
 * VERSION: 2.0 - Fixed persona label matching
 */

console.log('🔧 survivalAnalysisService.js loaded - VERSION 2.0');

// Map frontend filter values to backend API format
const mapFiltersToAPI = (filters, activeIndication) => {
  // Map indication
  const indication = activeIndication === 'NHL' ? 'nhl' : 'all';

  console.log('🗺️ Mapping indication:', {
    activeIndication,
    mappedTo: indication,
    isNHL: activeIndication === 'NHL'
  });

  // Map analysis type - backend expects 'analysis_type', not 'endpoint'
  const analysis_type = filters.analysisType.toLowerCase(); // 'pfs' or 'os'

  // Map timeline to months - extract number from timeline string
  const timelineMonths = parseInt(filters.timeline.split(' ')[0]); // "12 Month" -> 12

  // Legacy filter mapping (for backward compatibility)
  let filter_type = 'global';
  let filter_value = 'all';

  if (filters.activeLevel === 'institute' && filters.selectedInstitute) {
    filter_type = 'account';
    filter_value = filters.selectedInstitute;
  } else if (filters.activeLevel === 'region' && filters.selectedRegion) {
    filter_type = 'zone';
    filter_value = filters.selectedRegion;
  } else if (filters.activeLevel === 'physician' && filters.selectedPhysician) {
    filter_type = 'hcp';
    filter_value = filters.selectedPhysician;
  } else if (filters.activeLevel === 'my_patients' && filters.myPhysicianName) {
    // "My Patients" — filter by the logged-in physician's own name
    filter_type = 'hcp';
    filter_value = filters.myPhysicianName;
  }
  // Map date filters to backend parameters
  let apheresis_cutoff = null;
  let last_date_of_followup_cutoff = null;

  // Date of Apheresis filter
  if (filters.apheresisCutoff) {
    const [month, year] = filters.apheresisCutoff.split(' ');
    const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth() + 1;
    const lastDay = new Date(year, monthNum, 0).getDate();
    apheresis_cutoff = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    console.log('📅 Apheresis cutoff:', filters.apheresisCutoff, '→', apheresis_cutoff);
  }

  // Last Date of Follow Up filter
  if (filters.followUpCutoff) {
    const [month, year] = filters.followUpCutoff.split(' ');
    const monthNum = new Date(Date.parse(month + " 1, 2000")).getMonth() + 1;
    const lastDay = new Date(year, monthNum, 0).getDate();
    last_date_of_followup_cutoff = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    console.log('📅 Follow-up cutoff:', filters.followUpCutoff, '→', last_date_of_followup_cutoff);
  }

  return {
    indication,
    analysis_type,
    filter_type,
    filter_value,
    lines_of_failure: "all",
    timeline_months: timelineMonths,
    // active_level: filters.activeLevel || 'global', // legacy path handles filtering correctly
    selected_institute: filters.selectedInstitute || '',
    selected_regions: filters.selectedRegion ? [filters.selectedRegion] : [],
    selected_physicians: filters.activeLevel === 'my_patients' && filters.myPhysicianName
      ? [filters.myPhysicianName]
      : (filters.selectedPhysician ? [filters.selectedPhysician] : []),
    // active_level: filters.activeLevel || 'global', // legacy path handles filtering correctly
    // Date filters - map to existing backend parameters
    apheresis_cutoff,
    last_date_of_followup_cutoff
  };
};

// Transform API response to match frontend data structure
const transformAPIResponse = (apiResponse, indication, analysisType, timelineMonths) => {
  console.log('🔄 transformAPIResponse called:', {
    indication,
    analysisType,
    timelineMonths,
    cohort_size: apiResponse.cohort_size,
    has_overall_km_object: !!apiResponse.overall_km_object,
    has_persona_km_objects: !!apiResponse.persona_km_objects,
    persona_count: apiResponse.persona_km_objects ? apiResponse.persona_km_objects.length : 0
  });

  // Log first few persona labels to debug matching issues
  if (apiResponse.persona_km_objects && apiResponse.persona_km_objects.length > 0) {
    console.log('📋 Sample persona labels from backend:',
      apiResponse.persona_km_objects.slice(0, 5).map(obj => ({
        label: obj.label,
        persona: obj.persona,
        n: obj.n
      }))
    );
  }

  // CRITICAL: Backend returns "overall_km_object" and "persona_km_objects" (with underscores)
  const { cohort_size, overall_km_object, persona_km_objects, total_infused_patients } = apiResponse;

  // DEBUG: Check if at_risk is present in overall_km_object
  console.log('🔍 Checking overall_km_object structure:', {
    exists: !!overall_km_object,
    keys: overall_km_object ? Object.keys(overall_km_object) : [],
    hasAtRisk: overall_km_object ? !!overall_km_object.at_risk : false,
    atRiskLength: overall_km_object?.at_risk ? overall_km_object.at_risk.length : 0,
    atRiskSample: overall_km_object?.at_risk ? overall_km_object.at_risk.slice(0, 5) : []
  });

  // DEBUG: Check if at_risk is present in persona_km_objects
  console.log('🔍 Checking persona_km_objects structure:', {
    exists: !!persona_km_objects,
    count: persona_km_objects ? persona_km_objects.length : 0,
    firstObjectKeys: persona_km_objects && persona_km_objects[0] ? Object.keys(persona_km_objects[0]) : [],
    firstHasAtRisk: persona_km_objects && persona_km_objects[0] ? !!persona_km_objects[0].at_risk : false,
    firstAtRiskLength: persona_km_objects && persona_km_objects[0]?.at_risk ? persona_km_objects[0].at_risk.length : 0,
    firstAtRiskSample: persona_km_objects && persona_km_objects[0]?.at_risk ? persona_km_objects[0].at_risk.slice(0, 5) : []
  });

  // Validate that backend data matches expected indication
  if (persona_km_objects && persona_km_objects.length > 0) {
    const firstLabel = persona_km_objects[0].label || '';
    // Scan ALL labels — the new B-ALL backend emits three independent
    // sub-groups per row ("MRD -ve", "MRD +ve", "Blast >=5%"), so the FIRST
    // label may be an MRD label with no "Blast". B-ALL is identified by
    // "MRD" or "Blast"; NHL by "IPI" or "Bulky/Non-Bulky".
    const allLabels = persona_km_objects
      .map(obj => obj.label || obj.persona || '')
      .join(' | ');
    const isBALLData = allLabels.includes('MRD') || allLabels.includes('Blast');
    const isNHLData = allLabels.includes('IPI') || allLabels.includes('Bulky') || allLabels.includes('Non-Bulky');

    console.log('🔍 Data validation:', {
      indication,
      firstLabel,
      isBALLData,
      isNHLData,
      mismatch: (indication === 'NHL' && isBALLData && !isNHLData) || (indication === 'B-ALL' && isNHLData && !isBALLData)
    });

    if (indication === 'NHL' && isBALLData && !isNHLData) {
      console.error('❌ INDICATION MISMATCH: Frontend expects NHL but backend returned B-ALL data!');
      console.error('❌ First label from backend:', firstLabel);
      console.error('❌ This will cause matching failures. Check your backend indication filter.');
      console.error('❌ Backend might not be respecting the indication parameter.');
      console.error('❌ Check that backend is filtering data correctly for indication="nhl"');
    } else if (indication === 'B-ALL' && isNHLData && !isBALLData) {
      console.error('❌ INDICATION MISMATCH: Frontend expects B-ALL but backend returned NHL data!');
      console.error('❌ First label from backend:', firstLabel);
      console.error('❌ This will cause matching failures. Check your indication filter.');
    }
  }

  if (indication === 'NHL') {
    return transformNHLData(cohort_size, overall_km_object, null, persona_km_objects, analysisType, timelineMonths, total_infused_patients);
  } else {
    return transformBALLData(cohort_size, overall_km_object, null, persona_km_objects, analysisType, timelineMonths, total_infused_patients);
  }
};

/**
 * Determine if a KM object represents a genuine 0% survival (all patients had events)
 * vs "timepoint not reached" (patients were censored before reaching the timepoint).
 *
 * Client rule (boundary column = first column where at_risk drops to 0):
 *   Show Hourglass: at_risk=0, censored=n (non-zero), events=n → patients censored
 *   Show 0%:        at_risk=0, censored=0, events=n (non-zero) → all patients had events
 *
 * Implementation note: The backend truncates x/y arrays when survival hits 0 (removes
 * the 0-value timepoint). The at_risk/events/censored arrays are computed ONLY for the
 * truncated timeline, so the boundary column (at_risk=0) may not appear in the response.
 *
 * Strategy: Check boundary values if available. If not (due to truncation), use the
 * last y-value to infer: if survival was heading to 0 (last y very low or events == n
 * at last point), it's genuine 0%.
 */
const isGenuineZeroSurvival = (kmObj, timelineMonths) => {
  if (!kmObj || !kmObj.x || !kmObj.y || kmObj.n === 0) return false;

  const maxTimeInData = kmObj.x[kmObj.x.length - 1];

  // If curve extends to or beyond the timepoint, it's not truncated — use the actual y value
  if (maxTimeInData >= timelineMonths) return false;

  // Curve ends before the selected timepoint.
  const lastAtRisk = (kmObj.at_risk && kmObj.at_risk.length > 0) ? kmObj.at_risk[kmObj.at_risk.length - 1] : null;
  const lastCensored = (kmObj.censored && kmObj.censored.length > 0) ? kmObj.censored[kmObj.censored.length - 1] : null;
  const lastEvents = (kmObj.events && kmObj.events.length > 0) ? kmObj.events[kmObj.events.length - 1] : null;

  // Case 1: Boundary column is available in the data (at_risk actually drops to 0)
  if (lastAtRisk === 0) {
    // Client rule: at_risk=0, censored=0, events>0 → genuine 0%
    if (lastCensored === 0 && lastEvents > 0) return true;
    // at_risk=0, censored>0 → hourglass
    return false;
  }

  // Case 2: Backend truncated the curve before the boundary column was added.
  // The truncation happens when survival hits 0, meaning ALL remaining patients
  // had events. Check: if cumulative events at the last point == n, all had events.
  if (lastEvents != null && lastEvents === kmObj.n) return true;

  // Case 3: Check if all patients had events (events = n - censored at last point)
  // at_risk at last point = n - events - censored. If at_risk is still > 0 but
  // events + censored will equal n at the NEXT timepoint (which was truncated).
  // Heuristic: if lastAtRisk + lastEvents + lastCensored == n AND lastCensored == 0,
  // then all remaining at_risk patients will have events at the truncated timepoint.
  if (lastAtRisk != null && lastCensored === 0 && lastAtRisk > 0) {
    // All remaining patients are at risk with no censoring — they'll all have events
    // at the next timepoint (which was truncated because survival hit 0)
    return true;
  }

  return false;
};

const transformNHLData = (cohortSize, overallKm, personaBuckets, kmObjects, analysisType, timelineMonths, totalInfusedPatients) => {
  console.log('🔄 Transforming NHL data:', {
    cohortSize,
    timelineMonths,
    kmObjectsCount: kmObjects ? kmObjects.length : 0,
    kmObjectsPreview: kmObjects ? kmObjects.slice(0, 3).map(obj => ({
      label: obj.label,
      n: obj.n,
      survival: obj.y && obj.y[timelineMonths] ? Math.round(obj.y[timelineMonths] * 100) : 'N/A'
    })) : []
  });

  // CRITICAL FIX: Backend returns "label" not "persona"
  const personaMap = {};
  if (kmObjects && Array.isArray(kmObjects)) {
    kmObjects.forEach(obj => {
      // Backend uses "label" field, not "persona"
      const personaLabel = obj.label || obj.persona;
      if (!personaLabel) {
        console.warn('⚠️ KM object missing label/persona:', obj);
        return;
      }

      // Extract survival rate at the selected timeline
      // Backend now always returns full 0-24 month data — direct lookup at timepoint
      const survivalAtTimeline = obj.y && obj.y.length > timelineMonths
        ? Math.round(obj.y[timelineMonths] * 100)
        : 0;

      // NEW SIMPLIFIED LOGIC: Check conditions at the evaluation timepoint directly
      // Since backend no longer truncates, we can read at_risk/censored/events at timelineMonths
      let forceHourglass = false;
      let genuineZero = false;

      if (obj.n > 0 && obj.at_risk && obj.x) {
        const tpIdx = obj.x.indexOf(timelineMonths);
        if (tpIdx !== -1 && obj.at_risk[tpIdx] === 0) {
          const censoredAtTp = obj.censored ? obj.censored[tpIdx] : 0;
          const eventsAtTp = obj.events ? obj.events[tpIdx] : 0;
          if (censoredAtTp > 0 && eventsAtTp === 0) {
            // at_risk=0, censored>0, events=0 → hourglass (patients censored, no events)
            forceHourglass = true;
          } else if (censoredAtTp === 0 && eventsAtTp > 0) {
            // at_risk=0, censored=0, events>0 → genuine 0%
            genuineZero = true;
          }
          // If censored>0 AND events>0 → show actual survival % (x%)
        }
      }

      personaMap[personaLabel] = {
        pfs: forceHourglass ? 0 : survivalAtTimeline,
        os: forceHourglass ? 0 : survivalAtTimeline,
        n: obj.n || 0,
        genuineZero,
        forceHourglass
      };
      console.log(`📊 Mapped persona: "${personaLabel}" -> n=${obj.n}, survival=${survivalAtTimeline}%, genuineZero=${genuineZero}, forceHourglass=${forceHourglass}`);
    });
  } else {
    console.error('❌ kmObjects is not an array:', kmObjects);
  }

  // Transform to frontend structure
  const rows = [];

  // Group personas by relapse type - IMPORTANT: Match backend naming exactly
  // Backend format: "Primary Refractory | Non-Bulky | IPI Low (0-2)"
  const refractory = Object.keys(personaMap).filter(p => p.includes('Primary Refractory'));
  const earlyRelapse = Object.keys(personaMap).filter(p => p.includes('Early Relapsed'));
  const lateRelapse = Object.keys(personaMap).filter(p => p.includes('Late Relapsed'));

  console.log('🔍 Grouped personas:', {
    refractory: refractory.length,
    earlyRelapse: earlyRelapse.length,
    lateRelapse: lateRelapse.length,
    refractoryPersonas: refractory,
    earlyRelapsePersonas: earlyRelapse,
    lateRelapsePersonas: lateRelapse
  });

  if (refractory.length > 0) {
    rows.push(createNHLRow('refractory', 'Primary Refractory', 'No Response to Frontline Treatment', refractory, personaMap));
  }

  if (earlyRelapse.length > 0) {
    rows.push(createNHLRow('early_relapse', 'Early Relapsed', 'Relapse within 12M from last line of Therapy', earlyRelapse, personaMap));
  }

  if (lateRelapse.length > 0) {
    rows.push(createNHLRow('late_relapse', 'Late Relapsed', 'Relapse after 12M from last line of Therapy', lateRelapse, personaMap));
  }

  console.log('✅ NHL transformation complete:', {
    totalPatients: cohortSize,
    rowsCreated: rows.length,
    rowDetails: rows.map(r => ({ id: r.id, title: r.title, totalN: r.totalN }))
  });

  return {
    totalPatients: cohortSize,
    // Add overall cohort data from backend (using overall_km_object from new backend)
    overallCohort: overallKm ? {
      pfs: overallKm.y && overallKm.y[timelineMonths] ? Math.round(overallKm.y[timelineMonths] * 100) : 0,
      os: overallKm.y && overallKm.y[timelineMonths] ? Math.round(overallKm.y[timelineMonths] * 100) : 0,
      // IMPORTANT: Use cohortSize (filtered patient count) not overallKm.n (which may be global)
      // This ensures that when a physician has 0 patients, totalPatients correctly reflects 0
      totalPatients: cohortSize,
      n1: overallKm.n1 || 0, // Patients evaluable across all persona buckets
      kmData: cohortSize > 0 ? overallKm : null, // Only pass KM data when there are actual patients
      title: 'Overall Cohort',
      description: `Real-world evidence across all ${rows.length * 4} patient subgroups at ${timelineMonths} months`
    } : null,
    // IMPORTANT: Include the raw persona KM objects for card matching
    // Ensure each object has both 'label' and 'persona' fields for compatibility
    persona_km_objects: kmObjects ? kmObjects.map(obj => ({
      ...obj,
      persona: obj.label || obj.persona, // Ensure persona field exists
      label: obj.label || obj.persona    // Ensure label field exists
    })) : [],
    columns: [
      { id: 'nb_low', label: 'Low Risk', sub: 'Non-Bulky', type: 'non-bulky', range: '0-2' },
      { id: 'nb_high', label: 'High Risk', sub: 'Non-Bulky', type: 'non-bulky', range: '3-5' },
      { id: 'b_low', label: 'Low Risk', sub: 'Bulky', type: 'bulky', range: '0-2' },
      { id: 'b_high', label: 'High Risk', sub: 'Bulky', type: 'bulky', range: '3-5' },
    ],
    rows
  };
};

const createNHLRow = (id, title, desc, personas, personaMap) => {
  console.log(`🏗️ Creating NHL row: "${title}"`, {
    id,
    personasCount: personas.length,
    personas: personas,
    personaMapKeys: Object.keys(personaMap),
    personaMapSize: Object.keys(personaMap).length
  });

  const values = {
    nb_low: { pfs: 0, os: 0, n: 0 },
    nb_high: { pfs: 0, os: 0, n: 0 },
    b_low: { pfs: 0, os: 0, n: 0 },
    b_high: { pfs: 0, os: 0, n: 0 }
  };

  let totalN = 0;

  console.log(`  🔍 About to process ${personas.length} personas`);
  console.log(`  🔍 personaMap has ${Object.keys(personaMap).length} keys`);

  personas.forEach(persona => {
    console.log(`  🔍 Looking for persona: "${persona}"`);
    const data = personaMap[persona];

    if (!data) {
      console.error(`    ❌ ERROR: No data found in personaMap for persona: "${persona}"`);
      console.error(`    Available keys in personaMap:`, Object.keys(personaMap));
      console.error(`    Exact match check:`, Object.keys(personaMap).map(key => ({
        key,
        exactMatch: key === persona,
        similarity: key.includes(persona.split('|')[0].trim())
      })));
      return;
    }

    // Log if we found data but it has 0 patients
    if (data.n === 0) {
      console.warn(`    ⚠️ WARNING: Found persona but it has 0 patients!`, {
        persona,
        data
      });
    }

    totalN += data.n;

    console.log(`  📌 Processing persona: "${persona}"`, {
      n: data.n,
      pfs: data.pfs,
      os: data.os
    });

    // Map persona to column based on characteristics
    // Backend format: "Primary Refractory | Non-Bulky | IPI Low (0-2)"
    // Check for both hyphen types: regular hyphen (-) and en-dash (–)
    const isNonBulky = persona.includes('Non-Bulky');
    const isBulky = persona.includes('Bulky') && !persona.includes('Non-Bulky');
    const isLowRisk = persona.includes('Low (0-2)') || persona.includes('Low (0–2)');
    const isHighRisk = persona.includes('High (3-5)') || persona.includes('High (3–5)');

    if (isNonBulky && isLowRisk) {
      values.nb_low = data;
      console.log(`    ✅ Assigned to nb_low:`, data);
    } else if (isNonBulky && isHighRisk) {
      values.nb_high = data;
      console.log(`    ✅ Assigned to nb_high:`, data);
    } else if (isBulky && isLowRisk) {
      values.b_low = data;
      console.log(`    ✅ Assigned to b_low:`, data);
    } else if (isBulky && isHighRisk) {
      values.b_high = data;
      console.log(`    ✅ Assigned to b_high:`, data);
    } else {
      console.warn(`    ⚠️ Could not map persona to column:`, persona);
    }
  });

  console.log(`✅ Row "${title}" created:`, {
    totalN,
    values: {
      nb_low: values.nb_low.n,
      nb_high: values.nb_high.n,
      b_low: values.b_low.n,
      b_high: values.b_high.n
    }
  });

  return {
    id,
    title,
    desc,
    totalN,
    values
  };
};

const transformBALLData = (cohortSize, overallKm, personaBuckets, kmObjects, analysisType, timelineMonths, totalInfusedPatients) => {
  console.log('🔄 Transforming B-ALL data:', {
    cohortSize,
    timelineMonths,
    kmObjectsCount: kmObjects ? kmObjects.length : 0,
    kmObjectsPreview: kmObjects ? kmObjects.slice(0, 3).map(obj => ({
      label: obj.label,
      n: obj.n,
      survival: obj.y && obj.y[timelineMonths] ? Math.round(obj.y[timelineMonths] * 100) : 'N/A'
    })) : []
  });

  // CRITICAL FIX: Backend returns "label" not "persona"
  const personaMap = {};
  if (kmObjects && Array.isArray(kmObjects)) {
    kmObjects.forEach(obj => {
      // Backend uses "label" field, not "persona"
      const personaLabel = obj.label || obj.persona;
      if (!personaLabel) {
        console.warn('⚠️ KM object missing label/persona:', obj);
        return;
      }

      const survivalAtTimeline = obj.y && obj.y.length > timelineMonths
        ? Math.round(obj.y[timelineMonths] * 100)
        : 0;

      // NEW SIMPLIFIED LOGIC: Check conditions at the evaluation timepoint directly
      let forceHourglass = false;
      let genuineZero = false;

      if (obj.n > 0 && obj.at_risk && obj.x) {
        const tpIdx = obj.x.indexOf(timelineMonths);
        if (tpIdx !== -1 && obj.at_risk[tpIdx] === 0) {
          const censoredAtTp = obj.censored ? obj.censored[tpIdx] : 0;
          const eventsAtTp = obj.events ? obj.events[tpIdx] : 0;
          if (censoredAtTp > 0 && eventsAtTp === 0) {
            forceHourglass = true;
          } else if (censoredAtTp === 0 && eventsAtTp > 0) {
            genuineZero = true;
          }
        }
      }

      personaMap[personaLabel] = {
        pfs: forceHourglass ? 0 : survivalAtTimeline,
        os: forceHourglass ? 0 : survivalAtTimeline,
        n: obj.n || 0,
        genuineZero,
        forceHourglass
      };
      console.log(`📊 Mapped persona: "${personaLabel}" -> n=${obj.n}, survival=${survivalAtTimeline}%, genuineZero=${genuineZero}, forceHourglass=${forceHourglass}`);
    });
  } else {
    console.error('❌ kmObjects is not an array:', kmObjects);
  }

  // New B-ALL grid: 4 rows × 3 equal columns
  // Rows: Refractory|Early, Refractory|Late, Relapsed|Early, Relapsed|Late
  // Columns: MRD -ve, MRD +ve, Blast >=5% (three INDEPENDENT sub-groups)
  const rows = [];

  const refractoryEarly = Object.keys(personaMap).filter(p => p.includes('Refractory') && p.includes('Early'));
  const refractoryLate = Object.keys(personaMap).filter(p => p.includes('Refractory') && p.includes('Late'));
  const relapsedEarly = Object.keys(personaMap).filter(p => p.includes('Relapsed') && !p.includes('Refractory') && p.includes('Early'));
  const relapsedLate = Object.keys(personaMap).filter(p => p.includes('Relapsed') && !p.includes('Refractory') && p.includes('Late'));

  console.log('🔍 Grouped personas (MRD-based):', {
    refractoryEarly: refractoryEarly.length,
    refractoryLate: refractoryLate.length,
    relapsedEarly: relapsedEarly.length,
    relapsedLate: relapsedLate.length
  });

  if (refractoryEarly.length > 0) {
    rows.push(createBALLRow('refractory_early', 'Refractory', 'Early', '1-2 prior lines', refractoryEarly, personaMap));
  }
  if (refractoryLate.length > 0) {
    rows.push(createBALLRow('refractory_late', 'Refractory', 'Late', '3+ prior lines', refractoryLate, personaMap));
  }
  if (relapsedEarly.length > 0) {
    rows.push(createBALLRow('relapsed_early', 'Relapsed', 'Early', '1-2 prior lines', relapsedEarly, personaMap));
  }
  if (relapsedLate.length > 0) {
    rows.push(createBALLRow('relapsed_late', 'Relapsed', 'Late', '3+ prior lines', relapsedLate, personaMap));
  }

  console.log('✅ B-ALL transformation complete:', {
    totalPatients: cohortSize,
    rowsCreated: rows.length,
    rowDetails: rows.map(r => ({ id: r.id, title: r.title, totalN: r.totalN }))
  });

  return {
    totalPatients: cohortSize,
    overallCohort: overallKm ? {
      pfs: overallKm.y && overallKm.y[timelineMonths] ? Math.round(overallKm.y[timelineMonths] * 100) : 0,
      os: overallKm.y && overallKm.y[timelineMonths] ? Math.round(overallKm.y[timelineMonths] * 100) : 0,
      totalPatients: cohortSize,
      n1: overallKm.n1 || 0,
      kmData: cohortSize > 0 ? overallKm : null,
      title: 'Overall Cohort',
      description: `Real-world evidence across all ${rows.length * 3} patient subgroups at ${timelineMonths} months`
    } : null,
    persona_km_objects: kmObjects ? kmObjects.map(obj => ({
      ...obj,
      persona: obj.label || obj.persona,
      label: obj.label || obj.persona
    })) : [],
    columns: [
      { id: 'mrd_neg', label: 'MRD -ve', sub: 'MRD -ve', type: 'mrd_neg', range: null },
      { id: 'mrd_pos', label: 'MRD +ve', sub: 'MRD +ve', type: 'mrd_pos', range: null },
      { id: 'blast', label: 'Blast ≥5%', sub: 'Blast >=5%', type: 'blast', range: null }
    ],
    rows
  };
};

const createBALLRow = (id, title, linesLabel, linesDesc, personas, personaMap) => {
  console.log(`🏗️ Creating B-ALL row: "${title} | ${linesLabel}"`, {
    id,
    personasCount: personas.length,
    personas: personas
  });

  const values = {
    mrd_neg: { pfs: 0, os: 0, n: 0 },
    mrd_pos: { pfs: 0, os: 0, n: 0 },
    blast: { pfs: 0, os: 0, n: 0 }
  };

  let totalN = 0;

  personas.forEach(persona => {
    const data = personaMap[persona];

    console.log(`  📌 Processing persona: "${persona}"`, {
      n: data.n,
      pfs: data.pfs,
      os: data.os
    });

    // New backend format (three INDEPENDENT sub-groups per row):
    //   "Refractory | Early | MRD -ve"
    //   "Relapsed | Late | MRD +ve"
    //   "Relapsed | Late | Blast >=5%"
    // Note: MRD +ve and Blast >=5% are non-exclusive — a patient can appear
    // in both columns, so column n-counts may not sum to the row total.
    // totalN is therefore accumulated ONLY from the mutually-exclusive MRD
    // status columns (MRD -ve + MRD +ve); Blast >=5% is an overlay subset
    // and must NOT be added again or the row count double-counts.
    if (persona.includes('Blast >=5%') || persona.includes('Blast ≥5%')) {
      values.blast = data;
      console.log(`    ✅ Assigned to blast (overlay, excluded from totalN):`, data);
    } else if (persona.includes('MRD -ve')) {
      values.mrd_neg = data;
      totalN += data.n;
      console.log(`    ✅ Assigned to mrd_neg:`, data);
    } else if (persona.includes('MRD +ve')) {
      values.mrd_pos = data;
      totalN += data.n;
      console.log(`    ✅ Assigned to mrd_pos:`, data);
    } else {
      console.warn(`    ⚠️ Could not map persona to column:`, persona);
    }
  });

  console.log(`✅ Row "${title} | ${linesLabel}" created:`, {
    totalN,
    values: {
      mrd_neg: values.mrd_neg.n,
      mrd_pos: values.mrd_pos.n,
      blast: values.blast.n
    }
  });

  return {
    id,
    title,
    linesLabel,
    desc: linesDesc,
    totalN,
    values
  };
};

/**
 * Fetch survival analysis data from the backend
 */
export const fetchSurvivalAnalysis = async (filters, activeIndication) => {
  try {
    const apiPayload = mapFiltersToAPI(filters, activeIndication);
    const timelineMonths = parseInt(filters.timeline.split(' ')[0]); // Extract months for transformation

    console.log('🚀 Sending survival analysis request:', {
      apiPayload,
      timelineMonths,
      'filters.activeLevel': filters.activeLevel,
      'filters.selectedPhysician': filters.selectedPhysician,
      'filters.selectedRegion': filters.selectedRegion,
      'apiPayload.selected_physicians': apiPayload.selected_physicians,
      'apiPayload.selected_regions': apiPayload.selected_regions,
      'filters.timeline': filters.timeline
    });
    console.log('📍 Endpoint:', API_ENDPOINTS.survivalAnalysis);

    const response = await post(API_ENDPOINTS.survivalAnalysis, apiPayload);
    console.log('📦 API response received:', {
      cohort_size: response.cohort_size,
      overall_km: response.overall_km,
      filter_info: response.filter_info,
      persona_km_objects_count: response.persona_km_objects ? response.persona_km_objects.length : 0,
      first_persona_sample: response.persona_km_objects && response.persona_km_objects[0] ? {
        label: response.persona_km_objects[0].label,
        persona: response.persona_km_objects[0].persona,
        n: response.persona_km_objects[0].n
      } : null
    });

    const transformedData = transformAPIResponse(response, activeIndication, filters.analysisType, timelineMonths);
    console.log('🔄 Transformed data:', {
      totalPatients: transformedData.totalPatients,
      overallCohort: transformedData.overallCohort,
      timelineMonths,
      persona_km_objects_count: transformedData.persona_km_objects ? transformedData.persona_km_objects.length : 0,
      data_last_followup_updated: response.data_last_followup_updated
    });

    // Add the max follow-up date to the transformed data
    if (response.data_last_followup_updated) {
      transformedData.maxFollowUpDate = response.data_last_followup_updated;
    }

    return transformedData;
  } catch (error) {
    console.error('❌ Error fetching survival analysis:', error);
    throw error;
  }
};

/**
 * Check if the backend API is healthy
 */
export const checkAPIHealth = async () => {
  try {
    console.log('🔍 Checking API health at:', API_ENDPOINTS.health);
    const response = await get(API_ENDPOINTS.health);
    console.log('✅ API health response:', response);
    return response.status === 'ok';
  } catch (error) {
    console.error('❌ API health check failed:', error);
    return false;
  }
};

/**
 * Get Kaplan-Meier curve data for a specific cohort
 */
export const fetchKaplanMeierData = async (filters, activeIndication, cohortData) => {
  try {
    const apiPayload = mapFiltersToAPI(filters, activeIndication);
    const response = await post(API_ENDPOINTS.survivalAnalysis, apiPayload);

    // Check if this is overall cohort data (using overall_km_object from new backend)
    if (cohortData.title === 'Overall Cohort' && response.overall_km_object) {
      return {
        timeline: response.overall_km_object.x,
        survivalProbability: response.overall_km_object.y,
        patientCount: response.overall_km_object.n,
        persona: 'Overall Cohort - All Patients'
      };
    }

    // Find the specific KM object for individual cohorts (using persona_km_objects from new backend)
    const kmObject = response.persona_km_objects && response.persona_km_objects.find(obj => {
      const label = obj.label || obj.persona;
      return label && (label.includes(cohortData.title) || obj.n === cohortData.n);
    });

    if (kmObject) {
      return {
        timeline: kmObject.x,
        survivalProbability: kmObject.y,
        patientCount: kmObject.n,
        persona: kmObject.label || kmObject.persona
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching Kaplan-Meier data:', error);
    throw error;
  }
};

/**
 * Fetch ORR (Overall Response Rate) data from the backend.
 * Uses analysis_type: "orr" — completely separate from PFS/OS KM pipeline.
 * Returns: { orr_percent, total_day28_evaluated, response_counts } or null on failure.
 */
export const fetchORRData = async (filters, activeIndication) => {
  try {
    const indication = activeIndication === 'NHL' ? 'nhl' : 'all';

    // Use same legacy filter mapping as fetchSurvivalAnalysis — proven to work
    let filter_type = 'global';
    let filter_value = 'all';

    if (filters?.activeLevel === 'institute' && filters?.selectedInstitute) {
      filter_type = 'account';
      filter_value = filters.selectedInstitute;
    } else if (filters?.activeLevel === 'region' && filters?.selectedRegion) {
      filter_type = 'zone';
      filter_value = filters.selectedRegion;
    } else if (filters?.activeLevel === 'physician' && filters?.selectedPhysician) {
      filter_type = 'hcp';
      filter_value = filters.selectedPhysician;
    } else if (filters?.activeLevel === 'my_patients' && filters?.myPhysicianName) {
      filter_type = 'hcp';
      filter_value = filters.myPhysicianName;
    }

    const payload = {
      indication,
      analysis_type: 'orr',
      lines_of_failure: 'all',
      filter_type,
      filter_value,
      selected_institute: filters?.selectedInstitute || '',
      selected_regions: filters?.selectedRegion ? [filters.selectedRegion] : [],
      selected_physicians: filters?.activeLevel === 'my_patients' && filters?.myPhysicianName
        ? [filters.myPhysicianName]
        : (filters?.selectedPhysician ? [filters.selectedPhysician] : []),
    };

    // Apply apheresis cutoff if set
    if (filters?.apheresisCutoff) {
      const [month, year] = filters.apheresisCutoff.split(' ');
      const monthNum = new Date(Date.parse(month + ' 1, 2000')).getMonth() + 1;
      const lastDay = new Date(year, monthNum, 0).getDate();
      payload.apheresis_cutoff = `${year}-${String(monthNum).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    }

    console.log('🎯 Fetching ORR data:', payload);
    const response = await post(API_ENDPOINTS.survivalAnalysis, payload);

    if (response.analysis_type !== 'orr') {
      console.warn('⚠️ fetchORRData: unexpected response type', response.analysis_type);
      return null;
    }

    console.log('✅ ORR data received:', {
      orr_percent: response.orr_percent,
      total_day28_evaluated: response.total_day28_evaluated,
      response_counts: response.response_counts,
    });

    return {
      orr_percent: response.orr_percent,
      total_day28_evaluated: response.total_day28_evaluated,
      response_counts: response.response_counts,
      persona_orr_objects: response.persona_orr_objects || [],
    };
  } catch (error) {
    console.error('❌ fetchORRData failed:', error);
    return null;
  }
};
